import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Rating,
  Chip,
  Box,
  Pagination,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SelectChangeEvent,
  Avatar,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { PhotoCamera, Close, Visibility, Search } from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import { toast } from 'sonner';
import { apiGetReviews, Review, ReviewListParams } from '../../api/review.api';

const DATE_RANGE_OPTIONS = [
  { value: '7', label: '최근 7일' },
  { value: '30', label: '최근 30일' },
  { value: '90', label: '최근 90일' },
  { value: 'all', label: '전체' },
];

const ReviewList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // localStorage에서 사용자 정보를 가져와서 branchId 설정
  const getBranchIdFromUser = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        console.log('저장된 사용자 정보:', user);
        // user.branchInfo.id 또는 user.branch_id를 branchId로 사용
        const branchId = user.branchInfo?.id || user.branch_id;
        console.log('📍 브랜치 ID 추출:', { 
          userId: user.id, 
          branchInfoId: user.branchInfo?.id, 
          branchId: user.branch_id,
          finalBranchId: branchId 
        });
        return branchId;
      }
    } catch (error) {
      console.error('사용자 정보 파싱 오류:', error);
    }
    return null;
  };

  // URL 파라미터 또는 localStorage에서 branchId 가져오기
  const urlBranchId = Number(searchParams.get('branchId'));
  const userBranchId = getBranchIdFromUser();
  const branchId = urlBranchId || userBranchId;

  console.log(
    '리뷰 목록 페이지 - URL branchId:',
    urlBranchId,
    'User branchId:',
    userBranchId,
    'Final branchId:',
    branchId,
  );

  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState<ReviewListParams>({
    branchId: branchId || 0,
    serviceType: '',
    dateRange: '',
    searchText: '',
  });
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    type: 'before' | 'after';
  } | null>(null);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  useEffect(() => {
    if (!branchId || branchId === 0) {
      toast.error('올바르지 않은 접근입니다. 로그인이 필요합니다.');
      return;
    }

    // filters의 branchId 업데이트
    setFilters((prev) => ({ ...prev, branchId }));
    fetchReviews();
  }, [branchId, page]);

  useEffect(() => {
    // filters가 변경될 때만 API 호출
    if (filters.branchId && filters.branchId !== 0) {
      fetchReviews();
    }
  }, [filters]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await apiGetReviews({
        ...filters,
        page,
        limit,
      });
      setReviews(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('리뷰 조회 오류:', error);
      toast.error('리뷰 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 필터 초기화 핸들러
   */
  const handleResetFilters = () => {
    setFilters({
      branchId,
      serviceType: '',
      dateRange: '',
      searchText: '',
    });
    setPage(1);
    toast.success('필터가 초기화되었습니다.');
  };

  const handleFilterChange =
    (field: keyof ReviewListParams) =>
    (
      event:
        | SelectChangeEvent<string>
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setFilters((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      setPage(1);
    };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  const handleImageClick = (url: string, type: 'before' | 'after') => {
    setSelectedImage({ url, type });
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  const handleRowClick = (review: Review) => {
    setSelectedReview(review);
  };

  const handleCloseReviewDetail = () => {
    setSelectedReview(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // 모바일용 리뷰 카드 컴포넌트
  const MobileReviewCard = ({ review }: { review: Review }) => (
    <Card 
      sx={{ 
        mb: 2, 
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 3,
        },
        borderRadius: 2,
      }}
      onClick={() => handleRowClick(review)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {formatDate(review.createdAt)}
            </Typography>
            {(review.customerName || review.customerPhone) && (
              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                {review.customerName || review.customerPhone}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {review.beforeImageUrl && (
              <Avatar
                src={review.beforeImageUrl}
                sx={{ width: 32, height: 32, cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleImageClick(review.beforeImageUrl!, 'before');
                }}
              />
            )}
            {review.afterImageUrl && (
              <Avatar
                src={review.afterImageUrl}
                sx={{ width: 32, height: 32, cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleImageClick(review.afterImageUrl!, 'after');
                }}
              />
            )}
          </Box>
        </Box>
        
        {review.menu && (
          <Box sx={{ mb: 1 }}>
            <Chip 
              label={review.menu} 
              size="small" 
              variant="outlined" 
              sx={{ fontSize: '0.75rem' }}
            />
          </Box>
        )}
        
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {review.reviewText || '리뷰 내용이 없습니다.'}
        </Typography>
      </CardContent>
    </Card>
  );

  // DataTable용 데이터 구성
  const tableData = useMemo(() => {
    const columns = [
      {
        Header: (
          <Typography
            style={{ color: '#000000', fontSize: '14px', fontWeight: 'bold' }}
          >
            고객 정보
          </Typography>
        ),
        accessor: 'customerInfo',
        width: '15%',
      },
      {
        Header: (
          <Typography
            style={{ color: '#000000', fontSize: '14px', fontWeight: 'bold' }}
          >
            메뉴
          </Typography>
        ),
        accessor: 'menu',
        width: '15%',
      },
      {
        Header: (
          <Typography
            style={{ color: '#000000', fontSize: '14px', fontWeight: 'bold' }}
          >
            리뷰 내용
          </Typography>
        ),
        accessor: 'reviewText',
        width: '35%',
      },
      {
        Header: (
          <Typography
            style={{ color: '#000000', fontSize: '14px', fontWeight: 'bold' }}
          >
            식전 사진
          </Typography>
        ),
        accessor: 'beforeImage',
        width: '12%',
      },
      {
        Header: (
          <Typography
            style={{ color: '#000000', fontSize: '14px', fontWeight: 'bold' }}
          >
            식후 사진
          </Typography>
        ),
        accessor: 'afterImage',
        width: '12%',
      },
      {
        Header: (
          <Typography
            style={{ color: '#000000', fontSize: '14px', fontWeight: 'bold' }}
          >
            등록일
          </Typography>
        ),
        accessor: 'createdAt',
        width: '11%',
      },
    ];

    const rows = reviews.map((review, index) => ({
      id: review.id, // 리뷰 ID 추가
      reviewIndex: index, // 인덱스 추가
      customerInfo: (
        <Box
          sx={{
            minWidth: 120,
            height: '60px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {review.customerName || review.customerPhone ? (
            <Box>
              {review.customerName && (
                <Typography
                  variant='caption'
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100px',
                  }}
                  style={{
                    color: '#000000',
                    fontSize: '14px',
                    fontWeight: 'normal',
                  }}
                >
                  {review.customerName}
                </Typography>
              )}
              {review.customerPhone && (
                <Typography
                  variant='caption'
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100px',
                  }}
                  style={{
                    color: '#000000',
                    fontSize: '14px',
                    fontWeight: 'normal',
                  }}
                >
                  {review.customerPhone}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography
              variant='caption'
              style={{
                color: '#000000',
                fontSize: '14px',
                fontWeight: 'normal',
              }}
            >
              미입력
            </Typography>
          )}
        </Box>
      ),
      menu: (
        <Box
          sx={{
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant='button'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '120px',
            }}
            style={{ color: '#000000', fontSize: '14px', fontWeight: 'normal' }}
          >
            {review.menu || '-'}
          </Typography>
        </Box>
      ),
      reviewText: (
        <Box
          sx={{
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant='caption'
            sx={{
              maxWidth: 280,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
            style={{ color: '#000000', fontSize: '14px', fontWeight: 'normal' }}
            title={review.reviewText}
          >
            {review.reviewText || '-'}
          </Typography>
        </Box>
      ),
      beforeImage: (
        <Box
          sx={{
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {review.beforeImageUrl ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={review.beforeImageUrl}
                sx={{ width: 40, height: 40, cursor: 'pointer' }}
                onClick={() =>
                  handleImageClick(review.beforeImageUrl!, 'before')
                }
              />
              <IconButton
                size='small'
                onClick={() =>
                  handleImageClick(review.beforeImageUrl!, 'before')
                }
                sx={{ color: 'info.main' }}
              >
                <Visibility fontSize='small' />
              </IconButton>
            </Box>
          ) : (
            <Typography
              variant='caption'
              style={{
                color: '#000000',
                fontSize: '14px',
                fontWeight: 'normal',
              }}
            >
              -
            </Typography>
          )}
        </Box>
      ),
      afterImage: (
        <Box
          sx={{
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {review.afterImageUrl ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={review.afterImageUrl}
                sx={{ width: 40, height: 40, cursor: 'pointer' }}
                onClick={() => handleImageClick(review.afterImageUrl!, 'after')}
              />
              <IconButton
                size='small'
                onClick={() => handleImageClick(review.afterImageUrl!, 'after')}
                sx={{ color: 'info.main' }}
              >
                <Visibility fontSize='small' />
              </IconButton>
            </Box>
          ) : (
            <Typography
              variant='caption'
              style={{
                color: '#000000',
                fontSize: '14px',
                fontWeight: 'normal',
              }}
            >
              -
            </Typography>
          )}
        </Box>
      ),
      createdAt: (
        <Box
          sx={{
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant='caption'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            style={{ color: '#000000', fontSize: '14px', fontWeight: 'normal' }}
          >
            {formatDate(review.createdAt)}
          </Typography>
        </Box>
      ),
    }));

    return { columns, rows };
  }, [reviews]);

  if (!branchId) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      {/* 필터 섹션 */}
      <Box py={isMobile ? 2 : 3} px={isMobile ? 2 : 3}>
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            {/* 필터 제목 */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#1976d2',
                  fontWeight: 600,
                  fontSize: isMobile ? '1.1rem' : '1.25rem',
                }}
              >
                📋 리뷰 필터
              </Typography>
            </Box>

            {/* 필터 컨트롤 */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 2,
                alignItems: isMobile ? 'stretch' : 'center',
              }}
            >
              {/* 검색 필드 */}
              <Box sx={{ flex: 1, minWidth: isMobile ? '100%' : '300px' }}>
                <TextField
                  fullWidth
                  size={isMobile ? 'medium' : 'small'}
                  placeholder="리뷰 내용, 고객명 검색..."
                  value={filters.searchText}
                  onChange={handleFilterChange('searchText')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&:hover fieldset': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
              </Box>

              {/* 날짜 범위 필터 */}
              <Box sx={{ minWidth: isMobile ? '100%' : '140px' }}>
                <FormControl fullWidth size={isMobile ? 'medium' : 'small'}>
                  <InputLabel>기간</InputLabel>
                  <Select
                    value={filters.dateRange}
                    label="기간"
                    onChange={handleFilterChange('dateRange')}
                  >
                    <MenuItem value="">전체 기간</MenuItem>
                    {DATE_RANGE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* 초기화 버튼 */}
              <Box sx={{ minWidth: isMobile ? '100%' : 'auto' }}>
                <Button
                  variant="outlined"
                  onClick={handleResetFilters}
                  size={isMobile ? 'medium' : 'small'}
                  fullWidth={isMobile}
                  sx={{
                    color: '#2563eb',
                    borderColor: '#2563eb',
                    '&:hover': {
                      backgroundColor: '#eff6ff',
                      borderColor: '#1d4ed8',
                    },
                  }}
                >
                  🔄 초기화
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* 리뷰 목록 */}
        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            {loading ? (
              <Box textAlign='center' py={6}>
                <CircularProgress size={isMobile ? 30 : 40} />
                <Typography
                  variant='h6'
                  sx={{ 
                    mt: 2,
                    color: '#666666',
                    fontSize: isMobile ? '14px' : '16px'
                  }}
                >
                  로딩 중...
                </Typography>
              </Box>
            ) : reviews.length === 0 ? (
              <Box textAlign='center' py={6}>
                <Typography
                  variant='h6'
                  sx={{ 
                    color: '#757575',
                    fontSize: isMobile ? '14px' : '16px'
                  }}
                >
                  등록된 리뷰가 없습니다.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ 
                    color: '#9e9e9e',
                    fontSize: isMobile ? '12px' : '14px',
                    mt: 1
                  }}
                >
                  필터 조건을 변경해보세요.
                </Typography>
              </Box>
            ) : (
              <>
                {isMobile ? (
                  // 모바일 카드 레이아웃
                  <Box>
                    {reviews.map((review) => (
                      <MobileReviewCard key={review.id} review={review} />
                    ))}
                  </Box>
                ) : (
                  // 데스크톱 테이블 레이아웃
                  <Box
                    sx={{
                      '& .MuiTableContainer-root': {
                        borderRadius: 2,
                      },
                      '& .MuiTableRow-root': {
                        height: '60px !important',
                      },
                      '& .MuiTableCell-root': {
                        height: '60px !important',
                        padding: '8px 16px !important',
                        verticalAlign: 'middle !important',
                        borderBottom: '1px solid #e0e0e0 !important',
                      },
                      '& .MuiTableHead-root .MuiTableCell-root': {
                        backgroundColor: '#f5f5f5 !important',
                        height: '50px !important',
                        fontWeight: 'bold !important',
                      },
                    }}
                  >
                    <DataTable
                      table={tableData}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      onRowClick={(rowData: any) => {
                        const reviewIndex = rowData.reviewIndex;
                        if (reviewIndex !== undefined && reviews[reviewIndex]) {
                          console.log(
                            'Row clicked:',
                            reviewIndex,
                            reviews[reviewIndex],
                          );
                          handleRowClick(reviews[reviewIndex]);
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* 페이지네이션 */}
        {total > 0 && (
          <Box mt={3} display='flex' justifyContent='center'>
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={handlePageChange}
              color='primary'
              size={isMobile ? 'medium' : 'large'}
              showFirstButton={!isMobile}
              showLastButton={!isMobile}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#666666',
                  '&.Mui-selected': {
                    backgroundColor: '#1976d2',
                    color: 'white',
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>
      
      <Footer />

      {/* 리뷰 상세 보기 다이얼로그 */}
      <Dialog
        open={!!selectedReview}
        onClose={handleCloseReviewDetail}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: isMobile ? 0 : 3,
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant='h6'
            sx={{ 
              color: '#1976d2',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: '600'
            }}
          >
            📝 리뷰 상세 정보
          </Typography>
          <IconButton
            onClick={handleCloseReviewDetail}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          {selectedReview && (
            <Box sx={{ py: 2 }}>
              <Grid container spacing={isMobile ? 2 : 3}>
                {/* 고객 정보 섹션 */}
                {(selectedReview.customerName ||
                  selectedReview.customerPhone) && (
                  <Grid item xs={12}>
                    <Typography
                      variant='h6'
                      sx={{ 
                        mb: 2,
                        fontWeight: 600,
                        color: '#1976d2',
                        fontSize: isMobile ? '14px' : '16px'
                      }}
                    >
                      👤 고객 정보
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: 'grey.50',
                        padding: isMobile ? 1.5 : 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Grid container spacing={2}>
                        {selectedReview.customerName && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant='subtitle2'
                              gutterBottom
                              sx={{ 
                                color: '#666666',
                                fontSize: isMobile ? '12px' : '13px'
                              }}
                            >
                              고객 이름
                            </Typography>
                            <Typography
                              variant='body1'
                              sx={{ 
                                fontWeight: 500,
                                color: '#212121',
                                fontSize: isMobile ? '13px' : '14px'
                              }}
                            >
                              {selectedReview.customerName}
                            </Typography>
                          </Grid>
                        )}
                        {selectedReview.customerPhone && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant='subtitle2'
                              gutterBottom
                              sx={{ 
                                color: '#666666',
                                fontSize: isMobile ? '12px' : '13px'
                              }}
                            >
                              연락처
                            </Typography>
                            <Typography
                              variant='body1'
                              sx={{ 
                                fontWeight: 500,
                                color: '#212121',
                                fontSize: isMobile ? '13px' : '14px'
                              }}
                            >
                              {selectedReview.customerPhone}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                )}

                {/* 리뷰 기본 정보 섹션 */}
                <Grid item xs={12}>
                  <Typography
                    variant='h6'
                    sx={{ 
                      mb: 2,
                      fontWeight: 600,
                      color: '#1976d2',
                      fontSize: isMobile ? '14px' : '16px'
                    }}
                  >
                    📝 리뷰 정보
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant='subtitle2'
                    gutterBottom
                    sx={{ 
                      color: '#666666',
                      fontSize: isMobile ? '12px' : '13px'
                    }}
                  >
                    메뉴
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ 
                      mb: 2,
                      color: '#212121',
                      fontSize: isMobile ? '13px' : '14px'
                    }}
                  >
                    {selectedReview.menu || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant='subtitle2'
                    gutterBottom
                    sx={{ 
                      color: '#666666',
                      fontSize: isMobile ? '12px' : '13px'
                    }}
                  >
                    등록일
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ 
                      mb: 2,
                      color: '#212121',
                      fontSize: isMobile ? '13px' : '14px'
                    }}
                  >
                    {formatDate(selectedReview.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant='subtitle2'
                    gutterBottom
                    sx={{ 
                      color: '#666666',
                      fontSize: isMobile ? '12px' : '13px'
                    }}
                  >
                    리뷰 내용
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ 
                      mb: 3,
                      lineHeight: 1.6,
                      color: '#212121',
                      fontSize: isMobile ? '13px' : '14px',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {selectedReview.reviewText || '-'}
                  </Typography>
                </Grid>
                
                {/* 사진 섹션 */}
                {(selectedReview.beforeImageUrl ||
                  selectedReview.afterImageUrl) && (
                  <Grid item xs={12}>
                    <Typography
                      variant='h6'
                      sx={{ 
                        mb: 2,
                        fontWeight: 600,
                        color: '#1976d2',
                        fontSize: isMobile ? '14px' : '16px'
                      }}
                    >
                      📸 식전/식후 사진
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: 'grey.50',
                        padding: isMobile ? 1.5 : 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          gap: 3,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {selectedReview.beforeImageUrl && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant='subtitle2'
                              gutterBottom
                              sx={{ 
                                fontWeight: 600,
                                color: '#666666',
                                fontSize: isMobile ? '12px' : '13px'
                              }}
                            >
                              🍽️ 식전 사진
                            </Typography>
                            <img
                              src={selectedReview.beforeImageUrl}
                              alt='식전 사진'
                              style={{
                                width: isMobile ? '150px' : '200px',
                                height: isMobile ? '150px' : '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                transition: 'transform 0.2s ease',
                              }}
                              onClick={() =>
                                handleImageClick(
                                  selectedReview.beforeImageUrl!,
                                  'before',
                                )
                              }
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.02)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            />
                          </Box>
                        )}
                        {selectedReview.afterImageUrl && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant='subtitle2'
                              gutterBottom
                              sx={{ 
                                fontWeight: 600,
                                color: '#666666',
                                fontSize: isMobile ? '12px' : '13px'
                              }}
                            >
                              🍽️ 식후 사진
                            </Typography>
                            <img
                              src={selectedReview.afterImageUrl}
                              alt='식후 사진'
                              style={{
                                width: isMobile ? '150px' : '200px',
                                height: isMobile ? '150px' : '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                transition: 'transform 0.2s ease',
                              }}
                              onClick={() =>
                                handleImageClick(
                                  selectedReview.afterImageUrl!,
                                  'after',
                                )
                              }
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.02)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* 이미지 상세 보기 다이얼로그 */}
      <Dialog
        open={!!selectedImage}
        onClose={handleCloseImage}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: isMobile ? 0 : 3,
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant='h6'
            sx={{ 
              color: '#1976d2',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: '600'
            }}
          >
            📸 {selectedImage?.type === 'before' ? '식전 사진' : '식후 사진'}
          </Typography>
          <IconButton
            onClick={handleCloseImage}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 1 : 2 }}>
          {selectedImage && (
            <img
              src={selectedImage.url}
              alt={selectedImage.type === 'before' ? '식전 사진' : '식후 사진'}
              style={{ 
                width: '100%', 
                height: 'auto', 
                borderRadius: '8px',
                maxHeight: isMobile ? '70vh' : 'none',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ReviewList;
